import React, { useEffect, useState } from "react";

import {
  FlexComp,
  FormattedText,
  ImageComponent,
  ParaTag,
  SpanTag,
} from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";

import {
  ORDER_DETAIL_V2_ACTION_IDS,
  ORDER_DETAIL_V2_ASSETS,
} from "../../constants";
import {
  Card,
  CardCtaButtonList,
  CardCtaDivider,
  CardPadding,
  InfoStrip,
  ListBox,
  ListRow,
  RowMeta,
  SectionHeaderIcon,
} from "../../OrderDetailV2.style";
import { useOrderDetailV2 } from "../../OrderDetailV2Context";
import type { OrderDetailV2TravellersProps, Traveller } from "../../types";
import { EditTravellersModal } from "../EditTravellersModal";
import { OrderDetailV2Tooltip } from "../OrderDetailV2Tooltip";
import { TextButton } from "../TextButton";
import {
  NudgeBadge,
  NudgeBadgeGroup,
  NudgeInfoIconButton,
  NudgeInfoTooltipWrap,
  TravellerDetailsMissingIcon,
  TravellerRowCta,
  TravellerRowHeader,
  TravellerTitleRow,
} from "./OrderDetailV2Travellers.style";

export const OrderDetailV2Travellers: React.FC<
  OrderDetailV2TravellersProps
> = ({
  icon,
  title,
  actionLabel,
  isActionDisabled,
  actionDisabledReason,
  persuasionText,
  nudge,
  travellers,
  formConfig,
  modalConfig,
  entities = [],
  actionList = [],
  isActionVisible = false,
}: OrderDetailV2TravellersProps) => {
  const {
    bookingId,
    refreshOrderDetails,
    consumeDeepLinkAction,
    registerOpenEditTravellers,
  } = useOrderDetailV2();
  const disabled = Boolean(isActionDisabled);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const device = useBreakpointV2();

  const openTravellersModal = () => {
    if (!disabled && formConfig) {
      setIsModalOpen(true);
    }
  };

  // Register opener so nudge (and other remote CTAs) can open this local modal.
  useEffect(() => {
    if (!formConfig) {
      registerOpenEditTravellers?.(null);
      return;
    }
    registerOpenEditTravellers?.(() => {
      if (!disabled) {
        setIsModalOpen(true);
      }
    });
    return () => registerOpenEditTravellers?.(null);
  }, [disabled, formConfig, registerOpenEditTravellers]);

  // Deeplink: ?action=OPEN_EDIT_ENTITY_FORM — same pattern as nominee/contact.
  useEffect(() => {
    if (disabled || !formConfig) return;
    const consumed = consumeDeepLinkAction?.(
      ORDER_DETAIL_V2_ACTION_IDS.OPEN_EDIT_ENTITY_FORM
    );
    if (consumed) {
      setIsModalOpen(true);
    }
  }, [disabled, formConfig, consumeDeepLinkAction]);

  const showNudgeBadge = Boolean(nudge?.text);
  const showNudgeInfo = Boolean(nudge?.description) && device === "lg";

  return (
    <Card>
      <CardPadding>
        <FlexComp
          alignItems="center"
          justifyContent="space-between"
          margin="0 0 10px"
        >
          <FlexComp alignItems="center" gap="8px" margin="0">
            {device === "lg" && (
              <SectionHeaderIcon>
                <ImageComponent src={icon} width="20px" height="20px" />
              </SectionHeaderIcon>
            )}
            <SpanTag
              fontSize="large"
              fontWeight="bold"
              color={device === "lg" ? theme.greyText : theme.blackText}
            >
              <FormattedText
                value={{
                  text: {
                    value: title,
                    styles: {
                      mobile: {
                        "font-size": "16px", // Mobile font size
                      },
                    },
                  },
                }}
              />
            </SpanTag>
            {(showNudgeBadge || showNudgeInfo) && (
              <NudgeBadgeGroup>
                {showNudgeBadge && (
                  <NudgeBadge $backgroundColor={nudge?.backgroundColor}>
                    {nudge?.text}
                  </NudgeBadge>
                )}
                {showNudgeInfo && (
                  <NudgeInfoTooltipWrap>
                    <OrderDetailV2Tooltip
                      message={nudge?.description || ""}
                      trigger="hover"
                      position="bottom"
                      width="280px"
                    >
                      <NudgeInfoIconButton
                        type="button"
                        aria-label={nudge?.description}
                      >
                        <ImageComponent
                          src={ORDER_DETAIL_V2_ASSETS.INFO_OUTLINE_ICON}
                          width="18px"
                          height="18px"
                        />
                      </NudgeInfoIconButton>
                    </OrderDetailV2Tooltip>
                  </NudgeInfoTooltipWrap>
                )}
              </NudgeBadgeGroup>
            )}
          </FlexComp>
          {device === "lg" && isActionVisible && (
            <OrderDetailV2Tooltip
              message={actionDisabledReason || ""}
              trigger="both"
              show={disabled}
              position="bottom"
              autoHideDelay={5000}
              disabled={!disabled || !actionDisabledReason}
            >
              <TextButton
                btnTxt={actionLabel}
                onClick={openTravellersModal}
                showDefaultRightIcon={false}
                disabled={disabled}
              />
            </OrderDetailV2Tooltip>
          )}
        </FlexComp>

        {persuasionText && device === "lg" && (
          <InfoStrip>
            <SpanTag fontSize="xs" color={theme.greyText7}>
              <FormattedText value={persuasionText} />
            </SpanTag>
          </InfoStrip>
        )}

        <ListBox>
          {travellers.map((t: Traveller, index: number) => {
            const {
              policyUrl,
              cta,
              title: travellerTitle,
              desc,
              icon: detailsMissingIcon,
              detailsMissing,
            } = t;
            const { label, icon: ctaIcon, isEnabled, isVisible } = cta || {};
            const showDetailsMissingIcon = Boolean(
              detailsMissing && detailsMissingIcon
            );

            const handleCtaClick = () => {
              if (policyUrl && isEnabled) {
                window.open(policyUrl, "_blank");
              }
            };
            return (
              <ListRow key={`${t?.id || "traveller"}-${index}`}>
                <TravellerRowHeader>
                  <TravellerTitleRow>
                    <ParaTag fontSize="md" fontWeight="bold">
                      <FormattedText
                        value={{
                          text: {
                            value: travellerTitle,
                            styles: {
                              mobile: {
                                "font-size": "14px",
                              },
                            },
                          },
                        }}
                      />
                    </ParaTag>
                    {showDetailsMissingIcon && (
                      <TravellerDetailsMissingIcon aria-hidden="true">
                        <ImageComponent
                          src={detailsMissingIcon}
                          width="16px"
                          height="16px"
                        />
                      </TravellerDetailsMissingIcon>
                    )}
                  </TravellerTitleRow>

                  {isVisible && policyUrl && (
                    <TravellerRowCta>
                      <TextButton
                        btnTxt={label}
                        leftIcon={ctaIcon}
                        disabled={!isEnabled}
                        onClick={handleCtaClick}
                        showDefaultRightIcon={false}
                      />
                    </TravellerRowCta>
                  )}
                </TravellerRowHeader>

                {desc && (
                  <RowMeta>
                    <SpanTag fontSize="sm" color={theme.greyText}>
                      <FormattedText
                        value={{
                          text: {
                            value: desc,
                            styles: {
                              mobile: {
                                "font-size": "12px",
                              },
                            },
                          },
                        }}
                      />
                    </SpanTag>
                  </RowMeta>
                )}
              </ListRow>
            );
          })}
        </ListBox>
      </CardPadding>

      {device === "sm" && (
        <>
          <CardCtaDivider />
          <CardCtaButtonList>
            {(actionList as Array<{ id: string; text: string }>).map(
              (action) => (
                <OrderDetailV2Tooltip
                  key={action.id}
                  message={actionDisabledReason || ""}
                  trigger="both"
                  show={disabled}
                  position="bottom"
                  autoHideDelay={5000}
                  disabled={!disabled || !actionDisabledReason}
                >
                  <TextButton
                    fullWidth={true}
                    btnTxt={action.text}
                    disabled={disabled}
                    onClick={openTravellersModal}
                  />
                </OrderDetailV2Tooltip>
              )
            )}
          </CardCtaButtonList>
        </>
      )}

      {formConfig && bookingId && (
        <EditTravellersModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          formConfig={formConfig}
          entities={entities}
          bookingId={bookingId}
          onSave={() => undefined}
          onEndorsementSuccess={refreshOrderDetails}
          modalTitle={modalConfig?.header?.title || "Edit Travellers"}
          saveButtonLabel={
            modalConfig?.footer?.cta?.[0]?.label || "SAVE DETAILS"
          }
        />
      )}
    </Card>
  );
};
